import { nameStandaloneLinks } from '@theme/component';

if (!customElements.get('dieux-accordion')) {
  class DieuxAccordion extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.applyStaticMode = this.applyStaticMode.bind(this);
      this.hasEditorSnapshot = false;
      this.editorExpandedItem = null;
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      this.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal,
      });
      this.addEventListener('keydown', this.handleKeydown, {
        signal: this.abortController.signal,
      });

      const staticMedia = this.dataset.staticMedia;
      if (staticMedia) {
        this.staticQuery = window.matchMedia(staticMedia);
        this.staticQuery.addEventListener('change', this.applyStaticMode, {
          signal: this.abortController.signal,
        });
      }

      let hasExpandedItem = false;
      this.items.forEach((item) => {
        const trigger = this.getTrigger(item);
        const shouldExpand = trigger?.getAttribute('aria-expanded') === 'true' && !hasExpandedItem;

        if (shouldExpand) hasExpandedItem = true;
        this.setItemExpanded(item, shouldExpand, { restoreFocus: false });
      });

      if (this.staticQuery) this.applyStaticMode();

      if (!this.closest('nav, [role="navigation"]')) {
        this.items.forEach((item) => {
          const panel = this.getPanel(item);
          if (panel) nameStandaloneLinks(panel, this.getTrigger(item));
        });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = undefined;
      if (this.staticQuery) {
        this.staticQuery = undefined;
        this.applyStaticMode();
      }
      this.hasEditorSnapshot = false;
      this.editorExpandedItem = null;
    }

    get isStatic() {
      return this.staticQuery?.matches === true;
    }

    applyStaticMode() {
      const isStatic = this.isStatic;
      this.toggleAttribute('data-static-active', isStatic);

      this.items.forEach((item) => {
        const trigger = this.getTrigger(item);
        const panel = this.getPanel(item);
        const staticLabel = item.querySelector('[data-accordion-static-label]');

        if (trigger) {
          trigger.tabIndex = isStatic ? -1 : 0;
          if (isStatic) {
            trigger.setAttribute('aria-hidden', 'true');
            trigger.removeAttribute('aria-expanded');
            trigger.removeAttribute('aria-controls');
          } else {
            trigger.removeAttribute('aria-hidden');
            if (panel?.id) trigger.setAttribute('aria-controls', panel.id);
          }
        }

        if (staticLabel) {
          if (isStatic) {
            staticLabel.removeAttribute('aria-hidden');
          } else {
            staticLabel.setAttribute('aria-hidden', 'true');
          }
        }

        if (isStatic) {
          if (panel) panel.inert = false;
        } else {
          this.setItemExpanded(item, false, { restoreFocus: false });
        }
      });
    }

    get items() {
      return Array.from(this.children).filter(
        (child) => child instanceof HTMLElement && child.matches('[data-accordion-item]')
      );
    }

    getTrigger(item) {
      const trigger = item?.querySelector('[data-accordion-trigger]');
      return trigger instanceof HTMLButtonElement ? trigger : null;
    }

    getPanel(item) {
      const panel = item?.querySelector('[data-accordion-panel]');
      return panel instanceof HTMLElement ? panel : null;
    }

    isItemExpanded(item) {
      return this.getTrigger(item)?.getAttribute('aria-expanded') === 'true';
    }

    setItemExpanded(item, expanded, options = {}) {
      const trigger = this.getTrigger(item);
      const panel = this.getPanel(item);

      if (!trigger || !panel) return;
      if (this.isStatic) {
        panel.inert = false;
        return;
      }

      const restoreFocus = options.restoreFocus !== false;
      if (!expanded && restoreFocus && panel.contains(document.activeElement)) {
        trigger.focus({ preventScroll: true });
      }

      panel.inert = !expanded;
      trigger.setAttribute('aria-expanded', String(expanded));
    }

    openItem(item, options = {}) {
      if (!(item instanceof HTMLElement) || !this.contains(item)) return;

      this.items.forEach((otherItem) => {
        this.setItemExpanded(otherItem, otherItem === item, options);
      });
    }

    closeItem(item, options = {}) {
      this.setItemExpanded(item, false, options);
    }

    handleClick(event) {
      if (this.isStatic || !(event.target instanceof Element)) return;

      const trigger = event.target.closest('[data-accordion-trigger]');
      if (!(trigger instanceof HTMLButtonElement) || trigger.closest('dieux-accordion') !== this) return;

      const item = trigger.closest('[data-accordion-item]');
      if (!(item instanceof HTMLElement)) return;

      if (this.isItemExpanded(item)) {
        this.closeItem(item);
      } else {
        this.openItem(item);
      }
    }

    handleKeydown(event) {
      if (this.isStatic || event.key !== 'Escape' || event.defaultPrevented) return;

      const item = event.target instanceof Element
        ? event.target.closest('[data-accordion-item]')
        : null;
      if (!(item instanceof HTMLElement) || item.closest('dieux-accordion') !== this) return;
      if (!this.isItemExpanded(item)) return;

      event.preventDefault();
      const trigger = this.getTrigger(item);
      this.closeItem(item);
      trigger?.focus({ preventScroll: true });
    }

    selectItem(item) {
      if (!(item instanceof HTMLElement) || !this.contains(item)) return;

      if (!this.hasEditorSnapshot) {
        this.editorExpandedItem = this.items.find((candidate) => this.isItemExpanded(candidate)) || null;
        this.hasEditorSnapshot = true;
      }

      this.openItem(item, { restoreFocus: false });
    }

    releaseEditorSelection() {
      if (!this.hasEditorSnapshot) return;

      const expandedItem = this.editorExpandedItem;
      this.hasEditorSnapshot = false;
      this.editorExpandedItem = null;

      this.items.forEach((item) => {
        this.setItemExpanded(item, item === expandedItem, { restoreFocus: false });
      });
    }
  }

  customElements.define('dieux-accordion', DieuxAccordion);
}
